/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/joy';
import { Add, ContentCopy, Delete as DeleteIcon, Edit } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { listPricingRules, deletePricingRule } from '../../../main/API/pricingRule';
import DeleteAlert from '../../../services/services_manager/views/widgets/DeleteAlert';
import DuplicatePricingRuleDialog from '../../../pricing_rules/views/widgets/DuplicatePricingRuleDialog';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { dataGridNativeCheckboxSlots } from '../../../main/components/dataGridNativeCheckboxSlots';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';
import { OmrAmount } from '../../../main/components/OmrAmount';

/** v6: `valueGetter(params)` only. v7+: `(value, row, column, apiRef)`. */
function valueGetterRow(arg1, arg2) {
  if (arg2 !== undefined) return arg2;
  return arg1?.row;
}

function formatBandRange(from, to, emptyMinLabel, emptyMaxLabel) {
  // Blank ends mean the band is open on that side.
  const min = from == null || from === '' ? emptyMinLabel : String(from);
  const max = to == null || to === '' ? emptyMaxLabel : String(to);
  return `${min} – ${max}`;
}

function dashIfEmpty(value) {
  // A blank code means the band matches any shipment value.
  if (value == null || value === '') return '—';
  return value;
}

export default function PricingPlanBandsTable({ planId }) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const numericPlanId = planId ? parseInt(planId, 10) : null;

  const [bands, setBands] = useState([]);
  const [bandsLoading, setBandsLoading] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedBand, setSelectedBand] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceRow, setDuplicateSourceRow] = useState(null);
  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkDeleteAlert, setBulkDeleteAlert] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeletedCount, setBulkDeletedCount] = useState(null);

  const fetchBands = useCallback(async () => {
    if (!Number.isFinite(numericPlanId) || !hasPermission(PERMISSIONS.PRICING_RULES_LIST)) {
      setBands([]);
      return;
    }
    setBandsLoading(true);
    try {
      const response = await listPricingRules({
        plan: numericPlanId,
        page: 1,
        page_size: 100,
        ordering: 'weight_from',
      });
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : [];
      setBands(list);
    } catch (err) {
      console.error(err);
      setBands([]);
    } finally {
      setBandsLoading(false);
    }
  }, [numericPlanId, hasPermission]);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  const deleteBand = async (row) => {
    if (!hasPermission(PERMISSIONS.PRICING_RULES_DELETE)) return;
    setDeleteLoading(true);
    try {
      const response = await deletePricingRule(row.id);
      if (response.status === 200 || response.status === 204) {
        setBands((prev) => prev.filter((item) => item.id !== row.id));
        setBulkDeletedCount(null);
        setSuccessDelete(true);
      } else {
        setFailedDelete(true);
        setDeleteErrorMsg(t('pricingRules.deleteFailedDetail', { detail: '' }));
      }
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setDeleteLoading(false);
    setDeleteAlert(false);
  };

  const bulkDeleteBands = async () => {
    if (!hasPermission(PERMISSIONS.PRICING_RULES_DELETE) || !selectionModel.length) return;
    setBulkDeleteLoading(true);
    const idsToDelete = [...selectionModel];
    try {
      await Promise.all(idsToDelete.map((id) => deletePricingRule(id)));
      const deletedIds = new Set(idsToDelete);
      setBands((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setBulkDeletedCount(idsToDelete.length);
      setSuccessDelete(true);
      setSelectionModel([]);
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setBulkDeleteLoading(false);
    setBulkDeleteAlert(false);
  };

  const openDuplicateDialog = (row) => {
    if (!hasPermission(PERMISSIONS.PRICING_RULES_CREATE)) return;
    setDuplicateSourceRow(row);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateSuccess = (copy) => {
    if (!copy?.id) {
      fetchBands();
      return;
    }
    navigate(`/PricingRules/${copy.id}/Edit`, {
      state: {
        pricingRuleJustDuplicated: true,
        returnTo: `/PricingPlans/${numericPlanId}`,
      },
    });
  };

  const deleteSuccessMessage = useMemo(() => {
    if (bulkDeletedCount != null && bulkDeletedCount > 1) {
      return t('pricingRules.bulkDeletedSuccess', { count: bulkDeletedCount });
    }
    return t('pricingRules.deletedSuccess');
  }, [bulkDeletedCount, t]);

  const emptyMin = t('pricingPlans.bandMinZero');
  const emptyMax = t('pricingPlans.bandMaxUnlimited');

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('trackingEvent.colId'), width: 70 },
      { field: 'code', headerName: t('pricingRules.colCode'), flex: 1.4, minWidth: 120 },
      { field: 'name', headerName: t('pricingRules.colName'), flex: 1.6, minWidth: 140 },
      { field: 'scope', headerName: t('pricingRules.colScope'), width: 90 },
      { field: 'strategy', headerName: t('pricingRules.colStrategy'), flex: 1.2, minWidth: 120 },
      {
        // Matches shipment pricing_tariff_code.
        field: 'tariff_code',
        headerName: t('pricingRules.colTariffCode'),
        flex: 1.2,
        minWidth: 170,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.tariff_code),
      },
      {
        // Matches shipment product_category_code.
        field: 'category_code',
        headerName: t('pricingRules.colCategoryCode'),
        flex: 1.3,
        minWidth: 190,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.category_code),
      },
      {
        field: 'weight_band',
        headerName: t('pricingPlans.colWeightBand'),
        flex: 1.2,
        minWidth: 120,
        sortable: false,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return formatBandRange(row?.weight_from, row?.weight_to, emptyMin, emptyMax);
        },
      },
      {
        field: 'cbm_band',
        headerName: t('pricingPlans.colCbmBand'),
        flex: 1.2,
        minWidth: 120,
        sortable: false,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return formatBandRange(row?.cbm_from, row?.cbm_to, emptyMin, emptyMax);
        },
      },
      {
        field: 'unit_price',
        headerName: t('pricingRules.colUnitPrice'),
        width: 110,
        sortable: false,
        renderCell: (params) => <OmrAmount value={params.row.unit_price} empty="—" />,
      },
      {
        field: 'total_price',
        headerName: t('pricingRules.colTotalPrice'),
        width: 110,
        sortable: false,
        renderCell: (params) => <OmrAmount value={params.row.total_price} empty="—" />,
      },
      {
        field: 'active',
        headerName: t('pricingRules.colActive'),
        width: 80,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.active ? t('serviceManager.priceYes') : t('serviceManager.priceNo');
        },
      },
      {
        field: 'actions',
        headerName: t('trackingEvent.colActions'),
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_UPDATE}>
              <Tooltip title={t('pricingRules.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/PricingRules/${params.row.id}/Edit`, '_blank');
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_CREATE}>
              <Tooltip title={t('pricingRules.tooltipDuplicate')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDuplicateDialog(params.row);
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_DELETE}>
              <Tooltip title={t('pricingRules.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBand(params.row);
                    setDeleteAlert(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
          </Box>
        ),
      },
    ],
    [t, emptyMin, emptyMax]
  );

  if (!Number.isFinite(numericPlanId)) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography level="h3" textAlign="start">
            {t('pricingPlans.bandsTitle')}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
            {t('pricingPlans.bandsHint')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <PermissionGate permission={PERMISSIONS.PRICING_RULES_DELETE}>
            <Tooltip
              title={
                selectionModel.length === 0
                  ? t('pricingRules.tooltipBulkDeleteSelect')
                  : t('pricingRules.tooltipBulkDelete')
              }
            >
              <span>
                <Button
                  color="danger"
                  variant="soft"
                  startDecorator={<DeleteIcon />}
                  disabled={selectionModel.length === 0}
                  onClick={() => setBulkDeleteAlert(true)}
                >
                  {t('actions.delete')}
                </Button>
              </span>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.PRICING_RULES_CREATE}>
            <Button
              startDecorator={<Add />}
              onClick={() =>
                navigate(`/PricingRules/Add?plan=${numericPlanId}`, {
                  state: {
                    planContext: { id: numericPlanId },
                    returnTo: `/PricingPlans/${numericPlanId}`,
                  },
                })
              }
            >
              {t('pricingPlans.addBand')}
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      <PermissionGate permissions={[PERMISSIONS.PRICING_RULES_LIST]}>
        <Box sx={{ height: 420, width: '100%' }}>
          <DataGrid
            slots={dataGridNativeCheckboxSlots}
            rows={bands}
            columns={columns}
            loading={bandsLoading}
            getRowId={(row) => row.id}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={setSelectionModel}
            hideFooterSelectedRowCount
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
          />
        </Box>
      </PermissionGate>

      <DeleteAlert
        open={deleteAlert}
        loading={deleteLoading}
        onClose={() => setDeleteAlert(false)}
        onDelete={() => selectedBand && deleteBand(selectedBand)}
        title={t('pricingRules.deleteConfirmTitle')}
        body={t('pricingRules.deleteConfirmBody')}
        confirmLabel={t('actions.delete')}
      />

      <DeleteAlert
        open={bulkDeleteAlert}
        loading={bulkDeleteLoading}
        onClose={() => setBulkDeleteAlert(false)}
        onDelete={bulkDeleteBands}
        title={t('pricingRules.bulkDeleteConfirmTitle', { count: selectionModel.length })}
        body={t('pricingRules.bulkDeleteConfirmBody', { count: selectionModel.length })}
        confirmLabel={t('actions.delete')}
      />

      <DuplicatePricingRuleDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        sourceRule={duplicateSourceRow}
        onSuccess={handleDuplicateSuccess}
      />

      <Snackbar
        open={successDelete}
        autoHideDuration={3000}
        onClose={() => {
          setSuccessDelete(false);
          setBulkDeletedCount(null);
        }}
        color="success"
        variant="soft"
      >
        {deleteSuccessMessage}
      </Snackbar>
      <Snackbar
        open={failedDelete}
        autoHideDuration={5000}
        onClose={() => setFailedDelete(false)}
        color="danger"
        variant="soft"
      >
        {deleteErrorMsg || t('pricingRules.deleteError')}
      </Snackbar>
    </Box>
  );
}
